import { Button } from "@/components/ui/button";
import { ArrowRight, Clock, ClipboardList, Calendar, Building2, DollarSign, TrendingUp, Users, PiggyBank, GitBranch, Shield, FileText, BookOpen } from "lucide-react";
import { OptimizedImage } from "@/components/ui/optimized-image";
import type { AssessmentMeta } from "@/utils/assessmentLookup";
import a2IntroImage from "@/assets/a2-exit-timeline-intro.png";
import a3IntroImage from "@/assets/at-the-office-amico.png";
import a4IntroImage from "@/assets/a4-investment-data-intro.png";

const moduleIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'owner-readiness': ClipboardList,
  'owner-readiness-exit-timeline': Calendar,
  'business-profile': Building2,
  'business-valuation': DollarSign,
  'business-optimization': TrendingUp,
  'advisory-team': Users,
  'financial-readiness': PiggyBank,
  'successor-strategy': GitBranch,
  'governance-family': Shield,
  'exit-structure': FileText,
  'mysuccession-plan': BookOpen,
};

const moduleIntroImages: Record<string, string> = {
  'owner-readiness-exit-timeline': a2IntroImage,
  'business-profile': a3IntroImage,
  'business-valuation': a4IntroImage,
};

interface AssessmentIntroScreenProps {
  meta: AssessmentMeta;
  moduleId?: string;
  onBegin: () => void;
}

export function AssessmentIntroScreen({ meta, moduleId, onBegin }: AssessmentIntroScreenProps) {
  const illustrationSrc = (moduleId && moduleIntroImages[moduleId]) || "/lovable-uploads/aca40bb5-698e-4c57-b26d-c9838c7e03b6.png";
  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="flex flex-col md:flex-row items-center gap-8">
        {/* Left column — text */}
        <div className="flex-1 space-y-4">
          <h1 className="text-3xl font-bold text-foreground">
            {meta.title}
          </h1>

          {meta.description && (
            <p className="text-base text-muted-foreground leading-relaxed">
              {meta.description}
            </p>
          )}

          {meta.estimatedMinutes && (
            <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>{meta.estimatedMinutes} minutes</span>
            </div>
          )}

          <div className="pt-2">
            <Button
              onClick={onBegin}
              className="bg-primary hover:bg-primary/90 min-h-[48px] px-8 font-medium"
            >
              Begin Assessment
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Right column — illustration */}
        <div className="flex-shrink-0 hidden md:block">
          <OptimizedImage
            src={illustrationSrc}
            alt="Assessment illustration"
            className="w-64 h-auto object-contain"
            width={256}
            height={256}
          />
        </div>
      </div>
    </div>
  );
}
